import { memo, useMemo } from 'react'
import {
  ShieldCheck,
  ShieldAlert,
  CheckCircle2,
  Info,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLocaleFormat } from '../../../../i18n/useLocaleFormat'
import { type VlmValidationReportDictYMap } from '../../../../generated/nodeDataTypes'

const SEVERITY_COLORS: Record<string, string> = {
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  high: 'bg-red-500/20 text-red-400 border-red-500/30',
}

function scoreColor(score: number): string {
  if (score >= 8) return 'text-emerald-400'
  if (score >= 5) return 'text-yellow-400'
  return 'text-red-400'
}

function scoreBg(score: number): string {
  if (score >= 8) return 'bg-emerald-500/20 border-emerald-500/30'
  if (score >= 5) return 'bg-yellow-500/20 border-yellow-500/30'
  return 'bg-red-500/20 border-red-500/30'
}

export interface QualityTabContentProps {
  report: VlmValidationReportDictYMap | undefined
}

const QualityTabContent = memo(function QualityTabContent({
  report,
}: QualityTabContentProps) {
  const { t } = useTranslation('canvas')
  const { formatTime } = useLocaleFormat()

  const categoryLabels: Record<string, string> = useMemo(() => ({
    character_consistency: t('qualityTabContent.categoryCharacterConsistency'),
    anatomy: t('qualityTabContent.categoryAnatomy'),
    physics: t('qualityTabContent.categoryPhysics'),
    style_adherence: t('qualityTabContent.categoryStyleAdherence'),
    scene_continuity: t('qualityTabContent.categorySceneContinuity'),
    composition: t('qualityTabContent.categoryComposition'),
    artifacts: t('qualityTabContent.categoryArtifacts'),
  }), [t])

  if (!report) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-6 px-4 text-center">
        <ShieldCheck size={24} className="text-muted-foreground/50" />
        <p className="text-xs font-medium text-muted-foreground">
          {t('qualityTabContent.enableQualityCheck')}
        </p>
      </div>
    )
  }

  const score = report.overall_score ?? 0
  const issues = report.issues ?? []
  const passed = report.passed ?? true
  const summary = report.summary
  const validatedAt = report.validated_at
  const modelUsed = report.model_used

  return (
    <div className="flex flex-col gap-3 p-3 text-xs font-medium max-h-[280px] overflow-y-auto">
      {/* Header: Score + Pass/Fail */}
      <div className="flex items-center gap-2">
        <div
          className={`flex items-center gap-1.5 px-2 py-1 rounded-md border ${scoreBg(score)}`}
        >
          <span className={`text-sm font-semibold ${scoreColor(score)}`}>
            {score}/10
          </span>
        </div>
        {passed ? (
          <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/20 border border-emerald-500/30 text-emerald-400">
            <CheckCircle2 size={12} />
            <span>{t('qualityTabContent.passed')}</span>
          </div>
        ) : (
          <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/20 border border-red-500/30 text-red-400">
            <ShieldAlert size={12} />
            <span>{t('qualityTabContent.issuesFound')}</span>
          </div>
        )}
      </div>

      {/* Summary */}
      {summary && (
        <p className="text-muted-foreground leading-relaxed">{summary}</p>
      )}

      {/* Issues list */}
      {issues.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-muted-foreground font-medium">
            {t('qualityTabContent.issuesCount', { count: issues.length })}
          </span>
          {issues.map((issue, idx) => {
            const category = issue.category ?? ''
            const severity = issue.severity ?? ''
            const region = issue.region
            const description = issue.description
            return (
              <div
                key={idx}
                className="flex flex-col gap-1 p-2 rounded-md bg-muted/30 border border-border/50"
              >
                <div className="flex items-center gap-2">
                  <span className="text-foreground font-medium">
                    {categoryLabels[category] ?? category}
                  </span>
                  <span
                    className={`px-1.5 py-0.5 rounded text-xxs font-medium border ${
                      SEVERITY_COLORS[severity] ?? SEVERITY_COLORS.low
                    }`}
                  >
                    {severity}
                  </span>
                  {region && (
                    <span className="text-muted-foreground ml-auto">
                      {region}
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground">{description}</p>
              </div>
            )
          })}
        </div>
      )}

      {issues.length === 0 && (
        <div className="flex items-center gap-2 text-muted-foreground py-2">
          <Info size={14} />
          <span>{t('qualityTabContent.noIssuesDetected')}</span>
        </div>
      )}

      {/* Footer: timestamp + model */}
      {(validatedAt || modelUsed) && (
        <div className="flex items-center gap-2 text-xxs text-muted-foreground/70 pt-1 border-t border-border/30">
          {validatedAt && (
            <span>
              {formatTime(validatedAt)}
            </span>
          )}
          {modelUsed && <span>· {modelUsed}</span>}
        </div>
      )}
    </div>
  )
})

export default QualityTabContent
